import React from 'react';
import classNames from 'classnames';
import calcSecondsLeft from '@betgames/bg-tools/dist/utils/calcSecondsLeft';
import BgTimer from '@betgames/bg-tools/dist/utils/timer/Timer';
import { ITimerInstance, ITimerOptions } from '@betgames/bg-tools/dist/utils/timer/interfaces';
import { DateTime } from '../../enums/DateTime';
import { EMPTY_SPACE } from '../../constants/textSymbols';
import classes from './Timer.module.scss';

interface IProps {
    until: number;
    timestamp: number;
    textOnFinish?: string;
    onTick?: ITimerOptions['onTick'];
    onFinish?: ITimerOptions['onFinish'];
    onAfterExactSecond?: ITimerOptions['onAfterExactSecond'];
    format?: ITimerOptions['format'];
    showDurationUnits?: ITimerOptions['showDurationUnits'];
    blinkOn?: number;
    className?: string;
}

const defaultProps: Partial<IProps> = {
    textOnFinish: EMPTY_SPACE,
    blinkOn: 0,
    format: DateTime.DEFAULT_TIMER_FORMAT,
};

// TODO use TimerComponent from bg-tools - right now scss is missing
export const Timer: React.FC<IProps> = (props) => {
    const [blink, setBlink] = React.useState<boolean>(false);
    const spanRef = React.useRef<HTMLSpanElement>();
    const timer = React.useRef<ITimerInstance>();
    const mounted = React.useRef<boolean>(false);

    const setHtml = React.useCallback((html: string): void => {
        if (spanRef.current) {
            spanRef.current.innerHTML = html;
        }
    }, []);

    const onTick = React.useCallback((timeString: string, secondsLeft: number): void => {
        setHtml(timeString);

        if (props.onTick) {
            props.onTick(timeString, secondsLeft);
        }
    }, []);

    React.useEffect(() => {
        let onAfterExactSecond: ITimerOptions['onAfterExactSecond'];

        if (props.blinkOn) {
            onAfterExactSecond = {
                second: props.blinkOn,
                callback: () => {
                    setBlink(true);
                },
            };
        } else if (props.onAfterExactSecond) {
            ({ onAfterExactSecond } = props);
        }

        const onFinish = (): void => {
            setHtml(props.textOnFinish);
            setBlink(false);

            if (props.onFinish) {
                props.onFinish();
            }
        };

        timer.current = new BgTimer({
            until: calcSecondsLeft(props.until, props.timestamp),
            onAfterExactSecond,
            onTick,
            onFinish,
            format: props.format,
            showDurationUnits: props.showDurationUnits,
        });

        return () => {
            timer.current?.stop();
            timer.current = null;
        };
    }, []);

    React.useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }

        timer.current.update({ until: calcSecondsLeft(props.until, props.timestamp) });
    }, [props.timestamp]);

    return (
        <span
            ref={spanRef}
            className={classNames(props.className, {
                [classes.blink]: blink,
            })}
            data-qa="text-time-string"
        />
    );
};

Timer.defaultProps = defaultProps;
